import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Loader2, Send, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";

export default function InterviewSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/interview/${id}`);
      setData(r.data);
      if (r.data.status === 'completed') navigate(`/app/interview/${id}/review`, { replace: true });
    })();
  }, [id, navigate]);

  if (!data) return <div className="p-10"><Loader2 className="animate-spin" /></div>;
  const q = data.questions[idx];
  const total = data.questions.length;
  const setAns = (text) => setAnswers({ ...answers, [idx]: text });

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = data.questions.map((_, i) => ({ q_idx: i, text: answers[i] || "" }));
      await api.post(`/interview/${id}/submit`, { answers: payload });
      toast.success("Interview submitted. Generating feedback…");
      navigate(`/app/interview/${id}/review`);
    } catch (e) {
      apiErrorToast(e, "Submit failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen p-6 md:p-10 max-w-5xl mx-auto" data-testid="interview-session">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{data.level} · {data.role}</div>
          <h1 className="font-display font-black text-2xl tracking-tight mt-1">Question {idx+1} of {total}</h1>
        </div>
        <div className="text-sm text-zinc-500"><span className="font-mono">{Object.values(answers).filter(Boolean).length}</span> / {total} answered</div>
      </div>

      <div className="meter-track mb-8"><div className="meter-fill" style={{ width: `${((idx+1)/total)*100}%`, background: '#6366f1' }} /></div>

      <div className="card-flat p-6" data-testid={`question-${idx}`}>
        <div className="flex items-center flex-wrap gap-2 text-[10px] uppercase tracking-[0.2em]">
          <span className="text-zinc-500">{q.topic_category || q.category || "Question"}</span>
          {q.difficulty && <span className={`px-2 py-0.5 rounded ${q.difficulty==='hard'?'bg-red-100 text-red-700':q.difficulty==='medium'?'bg-amber-100 text-amber-700':'bg-emerald-100 text-emerald-700'}`}>{q.difficulty}</span>}
          {q.scenario && <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">Scenario{q.scenario_tag ? ` · ${q.scenario_tag}` : ''}</span>}
        </div>
        <div className="font-display font-bold text-xl mt-2 leading-snug">{q.question}</div>

        <textarea
          data-testid={`answer-textarea-${idx}`}
          value={answers[idx] || ""}
          onChange={(e)=>setAns(e.target.value)}
          rows={6}
          placeholder="Type your answer here. Think out loud, structure your thoughts, give examples…"
          className="mt-5 w-full p-4 border border-zinc-300 rounded-md focus:outline-none focus:border-indigo-500 text-sm leading-relaxed resize-y"
        />

        <div className="mt-5 flex items-center justify-between">
          <button data-testid="prev-question" disabled={idx===0} onClick={()=>setIdx(idx-1)} className="px-4 h-10 rounded-md border border-zinc-300 text-sm font-semibold disabled:opacity-40 flex items-center gap-1 hover:border-zinc-900"><ArrowLeft size={14} /> Previous</button>
          {idx < total-1 ? (
            <button data-testid="next-question" onClick={()=>setIdx(idx+1)} className="px-4 h-10 rounded-md bg-gradient-to-r from-indigo-500 to-sky-500 text-white text-sm font-semibold flex items-center gap-1">Next <ArrowRight size={14} /></button>
          ) : (
            <button data-testid="submit-interview" onClick={submit} disabled={submitting} className="px-5 h-10 rounded-md btn-gradient text-sm font-semibold flex items-center gap-1 disabled:opacity-50">
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Evaluating…</> : <>Submit for evaluation <Send size={14} /></>}
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-5 sm:grid-cols-10 gap-2">
        {data.questions.map((_, i) => (
          <button key={i} onClick={()=>setIdx(i)} data-testid={`jump-q-${i}`} className={`h-9 rounded-md text-xs font-mono font-semibold border transition ${
            i === idx ? 'bg-[#0E7C66] text-white border-[#0E7C66]'
              : answers[i] ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'border-zinc-200 text-zinc-500 hover:border-zinc-400'
          }`}>
            {answers[i] && i !== idx ? <CheckCircle2 size={12} className="inline" /> : i+1}
          </button>
        ))}
      </div>
    </div>
  );
}
